import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Globe, Eye } from "lucide-react";

/**
 * Live SERP Preview widget for SEO editing.
 * Shows how the page will appear in Google search results.
 */
export default function SERPPreview({ title, description, slug, baseUrl }) {
  const displayUrl = `${baseUrl || (typeof window !== "undefined" ? window.location.hostname : "example.com")}${slug ? `/${slug}` : ""}`;
  const displayTitle = (title || "").slice(0, 60) || "Page Title";
  const displayDesc = (description || "").slice(0, 160) || "Add a meta description to preview how this page appears in Google search results.";

  return (
    <div className="border border-border rounded-xl p-5 bg-card" data-testid="serp-preview">
      <div className="flex items-center gap-2 mb-3">
        <Eye className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Google Preview</span>
      </div>
      <div className="bg-white dark:bg-zinc-900 rounded-lg p-4 border border-border/50">
        <div className="flex items-center gap-2 mb-1">
          <Globe className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground truncate">{displayUrl}</span>
        </div>
        <h3 className="text-[#1a0dab] dark:text-blue-400 text-lg font-medium leading-tight mb-1 line-clamp-1" data-testid="serp-title">
          {displayTitle}
        </h3>
        <p className="text-sm text-[#4d5156] dark:text-zinc-400 leading-snug line-clamp-2" data-testid="serp-description">
          {displayDesc}
        </p>
      </div>
      <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
        <span>Title: {(title || "").length}/60</span>
        <span>Description: {(description || "").length}/160</span>
      </div>
    </div>
  );
}

/**
 * Complete SEO editor with fields + live SERP preview.
 */
export function SEOEditor({ seoTitle, seoDescription, ogImage, focusKeyword, slug, onChange }) {
  return (
    <div className="space-y-4" data-testid="seo-editor">
      <SERPPreview title={seoTitle} description={seoDescription} slug={slug} />

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label>SEO Title <span className="text-xs text-muted-foreground">(60 chars max)</span></Label>
          <Input
            value={seoTitle || ""}
            onChange={(e) => onChange("seo_title", e.target.value)}
            placeholder="Custom title for search engines"
            maxLength={60}
            data-testid="seo-title-input"
          />
        </div>
        <div className="sm:col-span-2">
          <Label>Meta Description <span className="text-xs text-muted-foreground">(160 chars max)</span></Label>
          <Textarea
            value={seoDescription || ""}
            onChange={(e) => onChange("seo_description", e.target.value)}
            placeholder="Custom description for search engine results"
            maxLength={160}
            rows={2}
            data-testid="seo-description-input"
          />
        </div>
        <div>
          <Label>OG Image URL</Label>
          <Input
            value={ogImage || ""}
            onChange={(e) => onChange("og_image", e.target.value)}
            placeholder="https://... (1200x630 recommended)"
            data-testid="seo-og-image-input"
          />
        </div>
        <div>
          <Label>Focus Keyword</Label>
          <Input
            value={focusKeyword || ""}
            onChange={(e) => onChange("focus_keyword", e.target.value)}
            placeholder="e.g., hyderabad city tour"
            data-testid="seo-focus-keyword-input"
          />
        </div>
      </div>
    </div>
  );
}
